import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import PanelLayout from '../layouts/PanelLayout';
import { documentService } from '../../services/documentService';
import Loader from '../../components/Loader';
import Button from '../../components/common/Button';
import ConfirmModal from '../../components/common/ConfirmModal';
import DateTimeDisplay from '../../components/common/DateTimeDisplay';
import Pagination from '../../components/common/Pagination';
import SearchableSelect from '../../components/common/SearchableSelect';
import ActionMenu from '../../components/common/ActionMenu';

const PUBLIC_BASE = process.env.REACT_APP_WEBSITE_URL || 'https://www.geekdas.in';

const StatusBadge = ({ status }) => (
   <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
         status === 'ACTIVE'
            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
      }`}
   >
      {status === 'ACTIVE' ? 'Active' : 'Inactive'}
   </span>
);

const isExpired = (expiresAt) => expiresAt && new Date(expiresAt) < new Date();

const Documents = () => {
   const navigate = useNavigate();
   const [documents, setDocuments] = useState([]);
   const [loading, setLoading] = useState(true);
   const [search, setSearch] = useState('');
   const [searchText, setSearchText] = useState('');
   const [statusFilter, setStatusFilter] = useState('');
   const [page, setPage] = useState(1);
   const debounceRef = useRef(null);
   const [totalPages, setTotalPages] = useState(1);
   const [total, setTotal] = useState(0);
   const [deleteTarget, setDeleteTarget] = useState(null);
   const [deleting, setDeleting] = useState(false);
   const [duplicating, setDuplicating] = useState(null);

   const fetchDocuments = useCallback(async () => {
      try {
         setLoading(true);
         const result = await documentService.getAll({
            page,
            limit: 10,
            search,
            ...(statusFilter && { status: statusFilter }),
         });
         setDocuments(result.documents);
         setTotalPages(result.totalPages);
         setTotal(result.total);
      } catch {
         toast.error('Failed to fetch documents');
      } finally {
         setLoading(false);
      }
   }, [page, search, statusFilter]);

   useEffect(() => {
      fetchDocuments();
   }, [fetchDocuments]);

   const handleSearchChange = (e) => {
      const value = e.target.value;
      setSearchText(value);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
         setSearch(value);
         setPage(1);
      }, 400);
   };

   const handleClearFilters = () => {
      setSearchText('');
      setSearch('');
      setStatusFilter('');
      setPage(1);
   };

   const hasActiveFilters = search || statusFilter;

   const handleDelete = async () => {
      if (!deleteTarget) return;
      try {
         setDeleting(true);
         await documentService.delete(deleteTarget.id);
         toast.success('Document deleted');
         setDeleteTarget(null);
         fetchDocuments();
      } catch {
         toast.error('Failed to delete document');
      } finally {
         setDeleting(false);
      }
   };

   const handleDuplicate = async (id) => {
      try {
         setDuplicating(id);
         await documentService.duplicate(id);
         toast.success('Document duplicated');
         fetchDocuments();
      } catch {
         toast.error('Failed to duplicate document');
      } finally {
         setDuplicating(null);
      }
   };

   const copyLink = (token) => {
      navigator.clipboard.writeText(`${PUBLIC_BASE}/document/${token}`);
      toast.success('Link copied to clipboard');
   };

   if (loading && documents.length === 0) {
      return (
         <PanelLayout>
            <Loader />
         </PanelLayout>
      );
   }

   return (
      <PanelLayout>
         <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
               <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Documents</h1>
                  <p className="mt-1 text-gray-600 dark:text-gray-400">
                     {total} document{total !== 1 ? 's' : ''} total
                  </p>
               </div>
               <Link to="/admin/documents/new">
                  <Button>New Document</Button>
               </Link>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
               <div className="relative">
                  <span className="absolute inset-y-0 left-3 flex items-center text-gray-400 dark:text-gray-500">
                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth={2}
                           d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                        />
                     </svg>
                  </span>
                  <input
                     type="text"
                     value={searchText}
                     onChange={handleSearchChange}
                     placeholder="Search by title..."
                     className="pl-9 pr-3 min-h-[42px] border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-primary text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-dark-accent focus:border-transparent text-sm w-64"
                  />
               </div>
               <div className="w-40">
                  <SearchableSelect
                     searchable={false}
                     placeholder="All Statuses"
                     options={[
                        { id: '', name: 'All Statuses' },
                        { id: 'ACTIVE', name: 'Active' },
                        { id: 'INACTIVE', name: 'Inactive' },
                     ]}
                     value={statusFilter}
                     onChange={(value) => {
                        setStatusFilter(value);
                        setPage(1);
                     }}
                     className="!mb-0"
                  />
               </div>
               {hasActiveFilters && (
                  <Button variant="secondary" onClick={handleClearFilters}>
                     Clear Filters
                  </Button>
               )}
            </div>

            {/* Table */}
            <div className="rounded-lg bg-white dark:bg-dark-secondary shadow-md overflow-hidden">
               <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                     <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                           {[
                              'Title',
                              'Status',
                              'Expiry',
                              'PIN',
                              'Views',
                              'Last Viewed',
                              'Created',
                              'Actions',
                           ].map((h) => (
                              <th
                                 key={h}
                                 className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                              >
                                 {h}
                              </th>
                           ))}
                        </tr>
                     </thead>
                     <tbody className="bg-white dark:bg-dark-secondary divide-y divide-gray-200 dark:divide-gray-700">
                        {loading ? (
                           <tr>
                              <td
                                 colSpan="8"
                                 className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                              >
                                 Loading...
                              </td>
                           </tr>
                        ) : documents.length === 0 ? (
                           <tr>
                              <td
                                 colSpan="8"
                                 className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                              >
                                 No documents found
                              </td>
                           </tr>
                        ) : (
                           documents.map((d) => (
                              <tr
                                 key={d.id}
                                 onClick={() => navigate(`/admin/documents/${d.id}`)}
                                 onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                       e.preventDefault();
                                       navigate(`/admin/documents/${d.id}`);
                                    }
                                 }}
                                 tabIndex={0}
                                 role="button"
                                 className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 dark:focus:ring-dark-accent"
                              >
                                 <td className="px-6 py-4">
                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                       {d.title}
                                    </div>
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex flex-col gap-1">
                                       <StatusBadge status={d.status} />
                                       {isExpired(d.expiresAt) && (
                                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                                             Expired
                                          </span>
                                       )}
                                    </div>
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {d.expiresAt ? (
                                       <DateTimeDisplay dateStr={d.expiresAt} />
                                    ) : (
                                       'Never'
                                    )}
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap">
                                    <span
                                       className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${d.pinEnabled ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' : 'text-gray-400 dark:text-gray-500'}`}
                                    >
                                       {d.pinEnabled ? 'PIN on' : 'No PIN'}
                                    </span>
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                    {d.viewCount}
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {d.lastViewedAt ? (
                                       <DateTimeDisplay dateStr={d.lastViewedAt} />
                                    ) : (
                                       'N/A'
                                    )}
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    <DateTimeDisplay dateStr={d.createdAt} />
                                 </td>
                                 <td
                                    className="px-6 py-4 whitespace-nowrap"
                                    onClick={(e) => e.stopPropagation()}
                                 >
                                    <ActionMenu
                                       items={[
                                          { label: 'Copy Link', onClick: () => copyLink(d.token) },
                                          {
                                             label: 'Duplicate',
                                             onClick: () => handleDuplicate(d.id),
                                             disabled: duplicating === d.id,
                                          },
                                          {
                                             label: 'Delete',
                                             variant: 'danger',
                                             onClick: () => setDeleteTarget(d),
                                          },
                                       ]}
                                    />
                                 </td>
                              </tr>
                           ))
                        )}
                     </tbody>
                  </table>
               </div>
            </div>

            <Pagination
               page={page}
               totalPages={totalPages}
               total={total}
               limit={10}
               onPageChange={setPage}
            />
         </div>

         <ConfirmModal
            isOpen={!!deleteTarget}
            title="Delete Document"
            message={`Are you sure you want to delete "${deleteTarget?.title}"? This cannot be undone.`}
            confirmLabel="Delete"
            confirmVariant="danger"
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
            loading={deleting}
         />
      </PanelLayout>
   );
};

export default Documents;
